import ArrowRight from '@/assets/arrowRightLight.svg';
import { useOptimizedPreview } from '../previews/OptimizedPreview';
import { ResponsiveImage } from '@/components';
import { ImageModel } from '@/types';
import { Tooltip } from '@/components';
interface SavedReportItemProps {
  report: {
    id?: number;
    name: string;
    contentType?: string;
    content?: {
      pageId?: string | number;
      pageTitle?: string;
      directory?: string;
      readTime?: string;
      publishedDate?: string | Date;
      pageUrl?: {
        text: string;
        href: string;
      };
      thumbnailImage?: ImageModel;
    };
    lastModified?: string;
    isBlob?: boolean;
    blobUri?: string | null;
    sel?: boolean;
    // Legacy properties for backward compatibility
    title?: string;
    thumbnailImage?: string | ImageModel;
    image?: ImageModel;
    category?: string;
    date?: string;
    href?: string;
  };
  selected: boolean;

  viewMode?: 'grid' | 'list';
}

const SavedReportItem = ({ report, selected, viewMode = 'grid' }: SavedReportItemProps) => {
  const { openReportPreview } = useOptimizedPreview();

  const createImageModel = (report: any): ImageModel => {
    const thumbnailImage = report?.content?.thumbnailImage;
    const legacyImage = typeof report?.thumbnailImage === 'string' ? report.thumbnailImage : report?.thumbnailImage;

    return {
      imageUrl: thumbnailImage?.imageUrl || legacyImage?.imageUrl || report?.image?.imageUrl || legacyImage || '',
      imageAlt:
        thumbnailImage?.imageAlt ||
        legacyImage?.imageAlt ||
        report?.content?.pageTitle ||
        report?.title ||
        'Report image',
      caption: thumbnailImage?.caption || legacyImage?.caption || '',
      copyrightText: thumbnailImage?.copyrightText || legacyImage?.copyrightText || '',
      mediaId: thumbnailImage?.mediaId || legacyImage?.mediaId || 0,
    };
  };

  if (!report) {
    return null;
  }

  const handleListReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const reportId = report?.content?.pageId?.toString();
    if (reportId) {
      openReportPreview(reportId);
    }
  };

  const handleListArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = report?.content?.pageUrl?.href || report?.href;
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (viewMode === 'list') {
    return (
      <div className="flex h-[160px] items-center gap-4 border border-gray-200 bg-white">
        <div
          className="h-full w-[315px] cursor-pointer border-r border-gray-300 bg-gray-50 px-[69px] py-0"
          onClick={handleListReportClick}
        >
          <ResponsiveImage
            image={createImageModel(report)}
            widths={{ xs: 550, sm: 768, md: 1024, lg: 1280, xl: 230, xxl: 230 }}
            heights={{ xs: 750, sm: 1024, md: 1334, lg: 1600, xl: 200, xxl: 200 }}
            imgProps={{ className: 'bottom-0 left-0 h-full object-contain p-0' }}
          />
        </div>

        <div className="relative bottom-10 flex-1 cursor-pointer" onClick={handleListReportClick}>
          <h4 className="font-bold text-gray-900">
            {report?.content?.pageTitle || report?.title || 'Untitled Report'}
          </h4>
          <div className="mt-1 text-xs text-black">
            {report?.content?.directory || report?.category || 'Uncategorized'}
          </div>
          <div className="mt-1 text-xs text-black">
            {report?.content?.publishedDate
              ? new Date(report.content.publishedDate).toLocaleDateString()
              : report?.date || 'No date'}
          </div>
        </div>
        <div className="relative flex h-full items-end pr-4 pb-1">
          <Tooltip content="Go to report" placement="top">
            <ArrowRight className="jumpButton peer w-5 cursor-pointer" onClick={handleListArrowClick} />
          </Tooltip>
        </div>
      </div>
    );
  }

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const reportId = report?.content?.pageId?.toString();
    if (reportId) {
      openReportPreview(reportId);
    }
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = report?.content?.pageUrl?.href || report?.href;
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div draggable="true" className="draggable-item">
      <div className={`item ${selected ? 'sel' : 'unsel'}`}>
        <div className="cmp">
          <div
            className="cntr relative h-0 w-full cursor-pointer overflow-hidden border border-b-0 border-gray-300 bg-gray-50 pt-[30px]"
            style={{ paddingBottom: '73.25%' }}
            onClick={handleReportClick}
          >
            <ResponsiveImage
              image={createImageModel(report)}
              widths={{ xs: 1, sm: 240, md: 1024, lg: 330, xl: 250, xxl: 230 }}
              heights={{ xs: 1, sm: 210, md: 1334, lg: 270, xl: 220, xxl: 200 }}
              imgProps={{ className: 'thumbnailCntr absolute bottom-0 left-0 h-full w-full object-contain' }}
            />
          </div>
          <div
            className="ifno relative h-[120px] cursor-pointer border border-gray-300 bg-white p-3 text-sm"
            onClick={handleReportClick}
          >
            <div className="reportTitle">
              <h4 className="font-bold">{report?.content?.pageTitle || report?.title || 'Untitled Report'}</h4>
              <div className="lnk mt-1 text-xs">
                {report?.content?.directory || report?.category || 'Uncategorized'}
              </div>
              <div className="lnk text-xs">
                {report?.content?.publishedDate
                  ? new Date(report.content.publishedDate).toLocaleDateString()
                  : report?.date || 'No date'}
              </div>
            </div>
            <div className="gotoReport absolute right-4 bottom-1.5">
              <Tooltip content="Go to report" placement="top">
                <ArrowRight
                  className="jumpButton peer margin-right:24px; w-5 cursor-pointer"
                  onClick={handleArrowClick}
                />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedReportItem;
